import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  TRANSFER,
  filterByCellValueMenuItem,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterColumnState,
  type ContextMenuOptions,
  type Transfer,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useClientSideTransfersFilter } from './useClientSideTransfersFilter';
import { useTransfersColumns, type AdditionalTransferColumns } from './useTransfersColumns';
import { useTransfersFilter, type TransfersBlotterFilter } from './useTransfersFilter';

export interface RecentTransfersParams {
  blotterID: string;
  defaultColumns?: BlotterColumnState[];
  defaultFilter?: TransfersBlotterFilter;
  initialIsOpen?: boolean;

  showCloneTab?: boolean;
  onCloneTab: () => void;
  contextMenuOptions?: ContextMenuOptions<Transfer>;
}

export function FilteredTransfers({
  blotterID,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  showCloneTab,
  onCloneTab,
  contextMenuOptions,
}: RecentTransfersParams) {
  const mixpanel = useMixpanel();
  const columns = useTransfersColumns(defaultColumns);

  const { additionalFilterState } = useContextBlotterFilter();
  // only show DateRangePicker if it's not used in the additionalFilterState
  const showDateRangePicker = !(additionalFilterState && 'DateRange' in additionalFilterState);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useTransfersFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { clientSideFilter, changeFilter, filterableProperties, filterBuilderProps } = filterResults;

  const clientSideTransfersFilter = useClientSideTransfersFilter(clientSideFilter);

  const dateRangeFilter = useDateRangeFilter(filterResults.filter, changeFilter, additionalFilterState?.DateRange);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<Transfer>) => [
      ...filterByCellValueMenuItem({
        params,
        openClause: filterBuilderAccordion.openClause,
        filterableProperties,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      ...(contextMenuOptions?.getCustomContextMenuItems?.(params) ?? []),
    ],
    [contextMenuOptions, filterBuilderAccordion.openClause, filterableProperties, mixpanel]
  );

  const request = useMemo(
    () => ({
      name: TRANSFER,
      tag: 'TransfersBlotter',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    columns: columns,
    rowID: 'rowID' satisfies keyof Transfer,
    clientLocalFilter: clientSideTransfersFilter,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      includeHiddenColumnsInQuickFilter: true,
    },
    exportOptions: {
      name: 'Transfers',
    },
  });

  const handleCloneTab = useCallback(() => {
    onCloneTab();
  }, [onCloneTab]);

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={showDateRangePicker}
        prefix={
          showCloneTab && (
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          )
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} rowEndSpacing="spacing12" />
    </>
  );
}

function onlyServerFilterKeys(filter: TransfersBlotterFilter | undefined) {
  if (!filter) {
    return filter;
  }

  return {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
  } satisfies TransfersBlotterFilter;
}

function colIDToFilterBuilderKey(id: string): keyof TransfersBlotterFilter | undefined {
  switch (id as keyof Transfer | AdditionalTransferColumns) {
    case 'Market':
      return 'Provider';
    case 'Currency':
      return 'Currency';
    case 'Status':
      return 'Status';
    case 'OwnerAccountID':
    case 'sourceAccount':
    case 'destinationAccount':
      return 'Account';
    case 'User':
      return 'Submitter';
    default:
      return undefined;
  }
}
